<template>
  <div>
    <validation-observer ref="userVO" v-slot="{ dirty }">
      <app-role-assignment ref="roleAssignment" :user="userData" :data="dataShared"
       @getGeneralAccountInfo="getDataAccount" />
      <section-title title="Información General de la Cuenta" has-line />
      <save-observer name="userSO" :dirty="dirty">
        <div class="row ml-1">
          <div class="col-md-3 col-sm-6">
            <div class="form-group">
              <label class="field-label">Nombre</label>
              <validation-provider :rules="'required'" v-slot="{ errors }">
                <custom-input v-if="!canEdit || hasEditPermission" v-model="userData.firstName" type="text"
                  maxlength="64" :error="errors[0]" custom-error-msg="Favor de entrar el nombre." />
                <label v-else>{{ userData.firstName }}</label>
              </validation-provider>
            </div>
          </div>
          <div class="col-md-1 col-sm-6">
            <div class="form-group">
              <label class="field-label">Inicial</label>
              <custom-input v-if="!canEdit || hasEditPermission" v-model="userData.middleInitial" type="text"
                maxlength="64" />
              <label v-else>{{ userData.middleInitial }}</label>
            </div>
          </div>
          <div class="col-md-4 col-sm-6">
            <div class="form-group">
              <label class="field-label">Apellido Paterno</label>
              <validation-provider :rules="'required'" v-slot="{ errors }">
                <custom-input v-if="!canEdit || hasEditPermission" v-model="userData.paternalLastName" type="text"
                  maxlength="64" :error="errors[0]" custom-error-msg="Favor de entrar el apellido paterno." />
                <label v-else>{{ userData.paternalLastName }}</label>
              </validation-provider>
            </div>
          </div>
          <div class="col-md-4 col-sm-6">
            <div class="form-group">
              <label class="field-label">Apellido Materno</label>
              <custom-input v-if="!canEdit || hasEditPermission" v-model="userData.maternalLastName" type="text"
                maxlength="64" />
              <label v-else>{{ userData.maternalLastName }}</label>
            </div>
          </div>
          <div class="col-md-4 col-sm-6 d-flex align-items-center">
            <div class="form-group w-100">
              <label class="field-label">Número Seguro Social</label>
              <validation-provider v-slot="{ errors }" ref="ssnValidator" :rules=" hasAnySpecialistRole ? 'ssn' : ''" vid="ssnVP" :custom-messages="{
                ssn: 'El número de seguro social entrado no es valido.',
              }">
                <custom-input v-if="canEditSSN" v-model="userData.socialSecurityNumber" type="text"
                  maxlength="11" @change="inputHandle" mask-type="Personalssn" :error="errors[0]" />
                <label v-else>***-**-{{ userData.socialSecurityNumber }}</label>
              </validation-provider>
            </div>
          </div>
          <div class="col-md-4 col-sm-6">
            <div class="form-group">
              <label class="field-label">Correo Electrónico</label>
              <validation-provider :rules="{
                required: isEmailRequired,
                email: true,
                regex: needTeacherEmailValidator() ? /@(de.pr.gov|miescuela.pr)$/ : '',
              }" v-slot="{ errors }" vid="emailVP" :custom-messages="{
                required: 'Favor de entrar el correo electrónico.',
                email: 'Favor de entrar un correo electrónico válido.',
                regex:
                  'El correo electrónico debe ser de la agencia de.pr.gov o miescuela.pr',
              }">
                <custom-input-email v-model="userData.email" type="email" maxlength="256" :error="errors[0]"
                  @input="nospace" @change="nospace" />
              </validation-provider>
            </div>
          </div>
          <div v-if="isRuaVisible" class="col-md-4 col-sm-6">
            <div class="form-group">
              <label class="field-label">RUA:</label>
              <validation-provider :rules="{ required }" v-slot="{ errors }" vid="rua">
                <custom-input v-model="userData.rua" type="text" maxlength="256" custom-error-msg="Campo requerido."
                  :error="errors[0]" />
              </validation-provider>
            </div>
          </div>
          <div v-if="requiresSifde" class="col-md-4 col-sm-6">
            <div class="form-group">
              <label class="field-label">Usuario SIFDE</label>
              <validation-provider v-slot="{ errors }" :custom-messages="{
                required: 'Favor de entrar el usuario SIFDE.',
              }">
                <custom-input v-model="userData.sifde" type="text" maxlength="256" :error="errors[0]" />
              </validation-provider>
            </div>
          </div>
        </div>
        <section-title title="Información de la Cuenta" has-line />
        <div class="row ml-1">
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label class="field-label">Nombre de Usuario</label>
              <validation-provider :disabled="inEditMode" :rules="'required'" v-slot="{ errors }" vid="usernameVP"
                :custom-messages="{
                  required: 'Favor de entrar el nombre de usuario.',
                }">
                <custom-input v-if="!inEditMode" v-model="userData.userName" type="text" maxlength="256"
                  :error="errors[0]" />
              </validation-provider>
              <label v-if="inEditMode">{{ userData.userName }}</label>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label class="field-label">Estatus</label>
              <validation-provider :rules="'required'" v-slot="{ errors }">
                <custom-radio class="inline-grey-radio" :options="userStatusOptions" name="userStatus"
                  :error="errors[0]" custom-error-msg="Favor seleccionar" :is-inline="true"
                  v-model="userData.activeFlag"></custom-radio>
              </validation-provider>
            </div>
          </div>
          <div class="col-md-4 col-sm-12">
            <div class="form-group">
              <label class="field-label">Autenticación</label>
              <validation-provider :rules="'required'" v-slot="{ errors }">
                <custom-radio class="inline-grey-radio" :options="authModeOptions" name="authMode" :error="errors[0]"
                  custom-error-msg="Favor seleccionar" :is-inline="true"
                  v-model="userData.authenticationMode"></custom-radio>
              </validation-provider>
            </div>
          </div>
        </div>
      </save-observer>
    </validation-observer>

    <div class="mt-3" v-if="hasCompanySpecialistLevel">
      <section-title title="Información de Corporaciones" has-line class="mt-3" />
      <div class="d-flex flex-wrap pl-4 ml-3 mt-4">
        <div v-for="(comp, index) in user.companies" :key="`comp-index-${index}`" class="
            chips-container
            border-color--gray-2
            border-radius
            px-3
            py-1
            mr-4
          ">
          <span class="color-black font-16 font-weight-normal">
            {{ comp.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppRoleAssignment from '@/components/common/user/AppRoleAssignment.vue';

import SectionTitle from 'SectionTitle';
import CustomInputEmail from '@/components/common/form/CustomInput'
import CustomInput from 'CustomInput';
import CustomRadio from '@/components/common/CustomRadio';
import SaveObserver from '@/components/common/SaveObserver';
import applicationIds from '@/utils/constants/applicationIds';
import locationAccessLevels from '@/utils/constants/locationAccessLevels.js';
import authenticationModes, {
  authModeOptions,
} from '@/utils/constants/authenticationModes.js';
import { extend } from 'vee-validate';
import Roles from '@/utils/constants/rolesIds';
import { isValidSsn } from '@/utils/helpers/ssn';
import { userMixin } from '@/modules/user/mixins/user.mixin.js';
import { getGeneralAccountData as _getGeneralAccountData } from '@/modules/userRequest/api';
import { getSysParamByName } from "@/modules/common/api";

extend('ssn', {
  validate: (value) => {
    if (!value) return false;

    const valueToValidate = value.replaceAll('-', '');
    return isValidSsn(valueToValidate);
  },
});

//Is required to existent error
extend('level.school', {
  validate: () => true
});

export default {
  name: 'UserGeneralTab',
  components: {
    SaveObserver,
    CustomInput,
    CustomRadio,
    SectionTitle,
    AppRoleAssignment,
    CustomInputEmail
  },
  mixins: { userMixin },
  model: {
    prop: 'user',
    event: 'user-changed',
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    inEditMode: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      userData: this.user,
      userStatusOptions: [
        { label: 'Activo', value: true },
        { label: 'No Activo', value: false },
      ],
      authModeOptions: authModeOptions,
      canSaveWithoutEmail: this.userHasPermissions(
        'security.user.cansavewithoutemail'
      ),
      canSeeSsn: this.userHasPermissions('security.user.canseesssn'),
      canEdit: this.inEditMode,
      ssnByInput: null,
      dataShared: {
        employee: null,
        positionNumber: null,
      },
      allianceSchoolsIds: [],
      isStatusChange: false,
    };
  },
  computed: {
    isRuaVisible() {
      return this.userData.roles
        .some(item =>
          [Roles.AbogadoDLEE, Roles.JuezAdministrativo, Roles.JuezCoordinador,Roles.AbogadoRepresentante].includes(item.roleId.toUpperCase()))
    },
    hasAnySpecialistRole() {
      return this.userData.roles.some(item =>
        [Roles.Especialista, Roles.EspecialistaRP].includes(item.roleId.toUpperCase()))
    },
    roleApplications() {
      let apps = [];
      this.userData.roles.forEach((e) => {
        apps.push(e.applicationId.toUpperCase());
      });
      return apps;
    },
    hasCompanySpecialistLevel() {
      return this.user?.roles.some(
        (s) => s.accessLevel === locationAccessLevels.CompanySpecialist
      );
    },
    isEmailRequired(){
      let isRequired = true;
      
      if(this.canSaveWithoutEmail || !this.userData.activeFlag)
        isRequired = false;

      return isRequired;
    },
    isTeacher() {
      const teacherRoles = [Roles.Maestro];

      // If it has roles and one of them is "Maestro", then it's a teacher
      return (
        this.user?.roles &&
        this.user?.roles?.some((x) =>
          teacherRoles.includes(x.roleId.toUpperCase())
        )
      );
    },
    hasEditPermission() {
      return this.userHasPermissions('security.user.canedit');
    },
    requiresSifde() {
      const caatRoles = [Roles.CaatRequirente, Roles.CAATTechnician];
      return (
        this.user?.roles &&
        this.user?.roles?.some((x) =>
          caatRoles.includes(x.roleId.toUpperCase())
        )
      );
    },
    canEditSSN(){
      const hasUserId = this.user?.userId?.length > 0;
      // if the user is being created then can edit the SSN.
      if(!hasUserId)
        return true;
    
      let canEdit = this.userHasPermissions('security.user.edit.ssn');
      
      return canEdit;
    },
  },
  watch: {
    user(newValue) {
      this.userData = newValue;
    },
    userData: {
      handler: function () {
        //set default authentication mode
        if (this.userData.userId && !this.userData.authenticationMode) {
          this.userData.authenticationMode = `${authenticationModes.Local}`;
        }
        this.$emit('user-changed', this.userData);
      },
      deep: true,
    },
    roleApplications: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        if (newVal != oldVal) {
          this.userData.canAddLocationInfo = this.roleApplications.some(
            (x) => x == applicationIds.mipe || x == applicationIds.financePortal
          );
        }
      },
    },
    requiresSifde: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.user.userData = null;
        }
      },
    },
    'userData.activeFlag': function() { 
        this.isStatusChange = true;
        this.$emit('status-change', this.isStatusChange);
      
    },
  },
  async mounted() {
    const { data } = await getSysParamByName({
      parameterNames: [
        'AllianceSchoolsIds'
      ],
    });
    this.allianceSchoolsIds = data[0].staticValue.split(',').map(x => Number(x))
  },
  methods: {
    inputHandle(ev) {
      let ssnWithoutHyphens = ev.replace(/-/g, '');
      this.ssnByInput = ssnWithoutHyphens;
      if (!this.ssnByInput || this.ssnByInput.length <= 8) return;
      let dataTosend = {
        socialNumber: this.ssnByInput,
        from: 'SocialNumber',
      };
      this.getDataAccount(dataTosend);
    },
    nospace(e) {
      this.userData.email = e?.trim()
    },
    async getDataAccount(info) {
      if (!this.isTeacher) {
        return;
      }

      try {
        const { data } = await _getGeneralAccountData(info);
        this.userData.firstName = data.firstName;
        this.userData.middleInitial = data.middleName;
        this.userData.paternalLastName = data.lastPaternalName;
        this.userData.maternalLastName = data.lastMaternalName;
        this.userData.socialSecurityNumber = data.socialSecurityNumber;
        this.dataShared.employee = data.kronosNumber;
        this.dataShared.positionNumber = data.positionNumber;
        this.canEdit = true;
      } catch (error) {
        this.Toast(
          'Alerta',
          'Favor de comunicarse con en el administrador para la creación.',
          'error'
        );
      }
    },
    needTeacherEmailValidator() {
      const roleAssignmentRef = this.$refs.roleAssignment ?? {};
      const userLocations = roleAssignmentRef?.academicProfile?.userLocations;
      // if the user is a teacher and has a school that is not part of the school alliances
      const needValidation = this.isTeacher  && !userLocations.map(c => c.schoolId).every(x => this.allianceSchoolsIds.includes(x));

      const rolesWhichNeedAgencyEmailValidation = [
        Roles.Director,
        Roles.Maestro,
        Roles.MaestroItinerante,
        Roles.MaestroIngles,
        Roles.MaestroJornadaParcial,
      ];
      const selectedRoles = roleAssignmentRef?.user?.roles ?? [];
      const roleIds = selectedRoles.map((x) => x.roleId.toUpperCase());

      return needValidation && rolesWhichNeedAgencyEmailValidation.some((roleId) => roleIds.includes(roleId));
    },
    async validate() {
      return this.$refs.userVO.validate();
    },
    saveProfiles() {
      return this.$refs.roleAssignment.saveProfiles();
    },
    setErrors(errors) {
      let showValidationToast = false;
      for (const error of errors) {
        switch (error.value) {
          case '6':
            this.$refs.userVO.setErrors({
              usernameVP: ['El nombre de usuario entrado ya existe.'],
            });
            showValidationToast = true;
            break;

          case '97':
          case '98':
            this.$refs.userVO.setErrors({
              ssnVP: [error.message],
            });
            showValidationToast = true;
            break;

          case '7':
            this.$refs.userVO.setErrors({
              emailVP: [error.message],
            });
            showValidationToast = true;
            break;
        }
      }
      return showValidationToast;
    },
  },
};
</script>

<style scoped lang="scss">
.chips-container {
  border-radius: 24px;
}
</style>
